import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Tag } from '@/types/article';
import { useToast } from '@/hooks/use-toast';

export function useTagManagement() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const createTag = async (name: string): Promise<Tag | null> => {
    setLoading(true);
    try {
      const slug = name.toLowerCase().replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
      
      const { data, error } = await supabase
        .from('tags')
        .insert({
          name,
          slug
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Tag created successfully"
      });

      return data as Tag;
    } catch (error) {
      console.error('Error creating tag:', error);
      toast({
        title: "Error",
        description: "Failed to create tag",
        variant: "destructive"
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteTag = async (tagId: string): Promise<void> => {
    setLoading(true);
    try {
      // First delete any article_tags relationships
      await supabase
        .from('article_tags')
        .delete()
        .eq('tag_id', tagId);

      // Then delete the tag itself
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Tag deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createTag,
    deleteTag,
    loading
  };
}